import React from 'react';
import { LockIcon, ShieldCheckIcon, CircleDashedIcon, SearchIcon } from 'lucide-react';
import type { OpportunityStatus, ClientDisclosure, ApplicationStatus } from '../../types/search';
import type { CandidateMatch } from '../../types/search';
import type { DocumentReviewStatus } from '../../types/platform';
import { applicationStatusLabels, documentReviewStatusLabels, opportunityStatusLabels } from '../../utils/labels';
import { matchBandLabel } from '../../utils/matching';
const chipBase = 'inline-flex items-center gap-1.5 font-sans text-micro uppercase tracking-[0.12em] px-2 py-1 border rounded-sm';

/**
 * Verification status. Deliberately independent of client disclosure so that
 * confidentiality can never imply an unconfirmed role is a live vacancy.
 */
export function OpportunityStatusChip({
  status,
  onDark
}: {
  status: OpportunityStatus;
  onDark?: boolean;
}) {
  const map: Record<OpportunityStatus, {
    cls: string;
    Icon: typeof ShieldCheckIcon;
  }> = {
    verified: {
      cls: 'bg-gold text-ink border-gold',
      Icon: ShieldCheckIcon
    },
    confidential_mandate: {
      cls: onDark ? 'bg-transparent text-gold-200 border-gold-600' : 'bg-charcoal text-parchment border-charcoal',
      Icon: LockIcon
    },
    proposed_opportunity: {
      cls: onDark ? 'bg-transparent text-navy-100 border-navy-300' : 'bg-white text-charcoal-500 border-navy-100',
      Icon: CircleDashedIcon
    },
    market_matched_opportunity: {
      cls: onDark ? 'bg-transparent text-navy-100 border-navy-300' : 'bg-white text-charcoal-500 border-navy-100',
      Icon: SearchIcon
    }
  };
  const {
    cls,
    Icon
  } = map[status];
  return <span className={`${chipBase} ${cls}`} data-id="element-534" data-mp="3l2yo18ica8m8">
      <Icon aria-hidden="true" className="h-3 w-3 shrink-0" data-id="element-535" data-mp="3pzyw1oyr1lfz" />
      {opportunityStatusLabels[status]}
    </span>;
}
export function DisclosureChip({
  disclosure,
  onDark
}: {
  disclosure: ClientDisclosure;
  onDark?: boolean;
}) {
  const withheld = disclosure === 'withheld_at_client_request';
  return <span className={`${chipBase} ${onDark ? 'bg-transparent text-navy-100 border-navy-400' : 'bg-parchment text-charcoal-500 border-parchment-300'}`} data-id="element-536" data-mp="1yz5wg92mpl19">
      {withheld ? 'Client withheld' : 'Client disclosed'}
    </span>;
}
export function MatchChip({
  band
}: {
  band: CandidateMatch['band'];
}) {
  if (band === 'below_threshold') return null;
  const strong = band === 'strong_potential_match';
  return <span className={`${chipBase} ${strong ? 'bg-navy-900 text-gold-200 border-navy-900' : 'bg-white text-navy-600 border-navy-200'}`} data-id="element-537" data-mp="2io7wxois4ol0">
      {matchBandLabel(band)}
    </span>;
}
const applicationTone: Partial<Record<ApplicationStatus, string>> = {
  not_selected: 'bg-white text-state-negative border-state-negative/40',
  withdrawn: 'bg-white text-charcoal-300 border-navy-100',
  on_hold: 'bg-white text-state-caution border-state-caution/40',
  offer_stage: 'bg-gold-100 text-gold-700 border-gold-400',
  search_completed: 'bg-white text-state-positive border-state-positive/40',
  candidate_consent_required: 'bg-navy-900 text-gold-200 border-navy-900',
  additional_information_requested: 'bg-white text-state-caution border-state-caution/40'
};
export function ApplicationStatusChip({
  status
}: {
  status: ApplicationStatus;
}) {
  return <span className={`${chipBase} ${applicationTone[status] ?? 'bg-parchment text-navy-700 border-parchment-300'}`} data-id="element-538" data-mp="2z4rhl343scez">
      {applicationStatusLabels[status]}
    </span>;
}
const documentTone: Partial<Record<DocumentReviewStatus, string>> = {
  revision_requested: 'bg-white text-state-caution border-state-caution/40',
  accepted_for_current_stage: 'bg-white text-state-positive border-state-positive/40',
  not_submitted: 'bg-white text-charcoal-300 border-navy-100',
  not_required: 'bg-white text-charcoal-300 border-navy-100',
  archived: 'bg-white text-charcoal-300 border-navy-100'
};
export function DocumentStatusChip({
  status
}: {
  status: DocumentReviewStatus;
}) {
  return <span className={`${chipBase} ${documentTone[status] ?? 'bg-parchment text-navy-700 border-parchment-300'}`} data-id="element-539" data-mp="3ltim4u2ripn8">
      {documentReviewStatusLabels[status]}
    </span>;
}
export function Tag({
  children,
  onDark
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return <span className={`inline-flex items-center font-sans text-[0.75rem] px-2 py-0.5 border rounded-sm ${onDark ? 'text-navy-100 border-navy-400' : 'text-charcoal-500 border-navy-100 bg-white'}`} data-id="element-540" data-mp="36ycbftsq22nk">
      {children}
    </span>;
}